import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft, Save, Printer, Loader2, FileOutput, Download, FileCheck, Mail } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import SendEmailModal from '../../components/SendEmailModal';
import { generateDocPdf } from '../../utils/generateDocPdf';
import AirWaybillSVGPrint from './AirWaybillSVGPrint';

const EMPTY_FORM = {
  job_no: '',
  master_awb_no: '',
  // Shipper
  shipper_name_address: '',
  shipper_account_no: '',
  // Consignee
  consignee_name_address: '',
  consignee_account_no: '',
  // Carrier / Issue
  not_negotiable: '',
  air_waybill_issued_by: '',
  issuing_agent_name_city: '',
  agent_iata_code: '14382410000',
  account_no: '',
  // Routing
  airport_of_departure: '',
  routing_to_1: '', routing_by_1: '',
  routing_to_2: '', routing_by_2: '',
  routing_to_3: '', routing_by_3: '',
  airport_of_destination: '',
  requested_flight: '',
  requested_date: '',
  // Accounting
  accounting_information: '',
  reference_number: '',
  optional_shipping_info: '',
  // Currency & Charges
  currency: 'INR',
  chgs_code: 'PP',
  ppd_wt: 'PPD',
  coll_wt: 'COL',
  ppd_other: '',
  coll_other: 'COLL',
  // Declared Values
  declared_value_carriage: 'NVD',
  declared_value_customs: '',
  amount_of_insurance: '',
  sci: '',
  // Handling
  handling_information: '',
  // Package Info
  no_of_pieces_rcp: '',
  gross_weight: '',
  kg_lb: 'KG',
  rate_class: '',
  commodity_item_no: '',
  chargeable_weight: '',
  rate_charge: 'AS AGREED',
  total: '',
  nature_quantity_short: '',
  nature_quantity_goods: '',
  // Other Charges - Prepaid
  weight_charge_prepaid: 'AS AGREED',
  valuation_charge_prepaid: '',
  tax_prepaid: 'AS AGREED',
  total_other_charges_agent_prepaid: 'AS AGREED',
  total_other_charges_carrier_prepaid: '',
  total_prepaid: 'AS AGREED',
  // Other Charges - Collect
  weight_charge_collect: '',
  valuation_charge_collect: '',
  tax_collect: '',
  total_other_charges_agent_collect: '',
  total_other_charges_carrier_collect: '',
  total_collect: '',
  // Additional
  currency_conversion_rates: '',
  cc_charges_dest_currency: '',
  for_carrier_use_destination: '',
  charges_at_destination: '',
  other_charges: '',
  // Signature
  signature_shipper: '',
  executed_on: new Date().toISOString().split('T')[0],
  at_place: '',
  signature_issuing_carrier: '',
  total_collect_charges: '',
};

const Field = ({ label, children, className = '' }) => (
  <div className={`space-y-1 ${className}`}>
    <Label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide font-ibm">{label}</Label>
    {children}
  </div>
);

const AirWaybillForm = () => {
  const { docketId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  // Detect House AWB mode from URL path
  const isHouse = location.pathname.includes('house-air-waybill');
  const DOC_TYPE = isHouse ? 'HOUSE_AIR_WAYBILL' : 'AIR_WAYBILL';
  const formTitle = isHouse ? 'House Air Waybill' : 'Master Air Waybill';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [documentId, setDocumentId] = useState(null);
  const [docketInfo, setDocketInfo] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const printContainerRef = useRef(null);
  const [showHiddenPrint, setShowHiddenPrint] = useState(false);
  const hiddenPrintPromiseResolve = useRef(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const getAuth = () => ({ 'Authorization': `Bearer ${localStorage.getItem('auth_token')}`, 'Content-Type': 'application/json' });

  // Compute charges fields from live docket payload (always fresh, not from saved AWB doc)
  const computeChargeFields = (data) => {
    const si = data?.docket?.payload_json?.shipment_info || {};
    const isPrepaid = (si.prepaid_collect || '').toLowerCase() === 'prepaid';

    const agentSum = Object.values(si.other_charges || {})
      .reduce((s, v) => s + (parseFloat(v) || 0), 0);
    const agentStr = agentSum > 0 ? agentSum.toFixed(2) : '';

    const carrierSum = Object.values(si.carrier_charges || {})
      .reduce((s, v) => s + (parseFloat(v) || 0), 0);
    const carrierStr = carrierSum > 0 ? carrierSum.toFixed(2) : '';

    // Always rebuild party text from live docket (strips branch_name)
    const partyText = (p) => p ? [p.name, p.address, p.phone && `Tel: ${p.phone}`, p.email && `Email: ${p.email}`].filter(Boolean).join('\n') : '';

    // Build "OTHER CHARGES" textarea: line 1 = agent charges, line 2 = carrier charges
    const formatCharges = (obj) =>
      Object.entries(obj || {})
        .filter(([, v]) => v && parseFloat(v) > 0)
        .map(([k, v]) => `${k}:${parseFloat(v).toFixed(2)}`)
        .join(',');

    const agentLine   = formatCharges(si.other_charges);
    const carrierLine = formatCharges(si.carrier_charges);
    const otherChargesText = [agentLine, carrierLine].filter(Boolean).join('\n');

    return {
      total_other_charges_agent_prepaid:   isPrepaid ? agentStr   : '',
      total_other_charges_agent_collect:   isPrepaid ? ''         : agentStr,
      total_other_charges_carrier_prepaid: isPrepaid ? carrierStr : '',
      total_other_charges_carrier_collect: isPrepaid ? ''         : carrierStr,
      shipper_name_address:    partyText(data?.resolved_parties?.exporter_shipper),
      consignee_name_address:  partyText(data?.resolved_parties?.importer_receiver),
      other_charges:           otherChargesText,
      agent_iata_code:         data?.company?.iata_code || '14382410000',
    };
  };

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const dr = await fetch(`${backendUrl}/api/dockets/${docketId}/print-data`, { headers: getAuth() });
        if (!dr.ok) throw new Error('Failed to fetch docket');
        const docketData = await dr.json();
        setDocketInfo(docketData);

        const chargeFields = computeChargeFields(docketData);

        const ar = await fetch(`${backendUrl}/api/dockets/${docketId}/documents/${DOC_TYPE}`, { headers: getAuth() });
        if (ar.ok) {
          const awbData = await ar.json();
          if (awbData?.id) {
            // Always overlay live charge totals on top of saved doc data
            setFormData({ ...awbData.document_data, ...chargeFields });
            setDocumentId(awbData.id);
            setIsSaved(true);
            return;
          }
        }
        populate(docketData);
      } catch (e) {
        toast({ title: 'Error', description: e.message, variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    };
    if (docketId) init();
  }, [docketId]);

  const populate = (data) => {
    const { docket, resolved_parties, resolved_shipment, company } = data;
    const payload = docket.payload_json || {};
    const si = payload.shipment_info || {};
    const hdr = payload.header || {};
    const invs = payload.invoices || [];
    const inv = invs[0] || {};

    const partyText = (p) => p ? [p.name, p.address, p.phone && `Tel: ${p.phone}`, p.email && `Email: ${p.email}`].filter(Boolean).join('\n') : '';
    const companyText = company ? [company.name, company.address, company.city, company.state].filter(Boolean).join(', ') : '';

    // For House AWB use HAWB fields; for Master AWB use MAWB fields
    const awbNo = isHouse ? (si.hawb_no || '') : (si.mawb_no || '');
    const awbDesc = isHouse ? (si.hawb_description || '') : (si.mawb_description || si.hawb_description || '');

    // Sum Other Charges Due Agent / Carrier — handled by computeChargeFields (called on every load)
    const agentChargesStr = '';
    const carrierChargesStr = '';
    const isPrepaid = (si.prepaid_collect || '').toLowerCase() === 'prepaid';

    setFormData({
      ...EMPTY_FORM,
      job_no: docket.system_docket_no || '',
      master_awb_no: awbNo,
      shipper_name_address: partyText(resolved_parties?.exporter_shipper),
      consignee_name_address: partyText(resolved_parties?.importer_receiver),
      not_negotiable: awbNo,
      air_waybill_issued_by: resolved_shipment?.carrier || si.airline || '',
      issuing_agent_name_city: companyText,
      agent_iata_code: company?.iata_code || '14382410000',
      airport_of_departure: resolved_shipment?.loading_from || '',
      routing_to_1: resolved_shipment?.discharge_code || resolved_shipment?.discharge_at || '',
      routing_by_1: resolved_shipment?.carrier || si.airline || '',
      airport_of_destination: resolved_shipment?.discharge_at || '',
      requested_flight: si.flight_no || '',
      requested_date: si.flight_date || '',
      accounting_information: hdr.notify_party_text || '',
      no_of_pieces_rcp: inv.no_of_packages || payload.package_info?.package_no_of_packages || '',
      gross_weight: inv.gross_weight || payload.package_info?.package_gross_weight || '',
      chargeable_weight: inv.cbm_chargeable_weight || payload.package_info?.package_cbm_chargeable_weight || '',
      nature_quantity_short: inv.description || '',
      nature_quantity_goods: inv.description || '',
      handling_information: hdr.remarks || awbDesc,
      signature_shipper: company?.name || '',
      signature_issuing_carrier: company?.name || '',
      executed_on: new Date().toISOString().split('T')[0],
      // Other Charges Due Agent — prepaid or collect based on Prepaid/Collect field
      total_other_charges_agent_prepaid:  isPrepaid ? agentChargesStr : '',
      total_other_charges_agent_collect:  isPrepaid ? '' : agentChargesStr,
      // Other Charges Due Carrier — same logic
      total_other_charges_carrier_prepaid:  isPrepaid ? carrierChargesStr : '',
      total_other_charges_carrier_collect:  isPrepaid ? '' : carrierChargesStr,
    });
  };

  const applyForwarderParties = () => {
    const comp = docketInfo?.company;
    const compText = comp ? [comp.name, comp.address, comp.city && `${comp.city}, ${comp.state || ''}`, comp.phone && `Tel: ${comp.phone}`, comp.email && `Email: ${comp.email}`].filter(Boolean).join('\n') : '';
    const rp = docketInfo?.resolved_parties || {};
    const hdr = docketInfo?.docket?.payload_json?.header || {};
    const si = docketInfo?.docket?.payload_json?.shipment_info || {};
    const agentParty = (hdr.importer_receiver_type === 'agent' ? rp.importer_receiver : null) || rp.agent || rp.notify_party;
    const partyText = (p) => p ? [p.name, p.address, p.phone && `Tel: ${p.phone}`, p.email && `Email: ${p.email}`].filter(Boolean).join('\n') : '';
    const agentText = partyText(agentParty) || hdr.agent_text || si.agent || (rp.importer_receiver ? partyText(rp.importer_receiver) : '');

    setFormData(prev => ({
      ...prev,
      shipper_name_address: compText || prev.shipper_name_address,
      consignee_name_address: agentText || prev.consignee_name_address,
    }));
    toast({ title: 'Parties Updated', description: 'Shipper set to Stavan, Consignee set to Destination Agent.' });
  };

  const applyClientParties = () => {
    const rp = docketInfo?.resolved_parties || {};
    const partyText = (p) => p ? [p.name, p.address, p.phone && `Tel: ${p.phone}`, p.email && `Email: ${p.email}`].filter(Boolean).join('\n') : '';
    setFormData(prev => ({
      ...prev,
      shipper_name_address: partyText(rp.exporter_shipper) || prev.shipper_name_address,
      consignee_name_address: partyText(rp.importer_receiver) || prev.consignee_name_address,
    }));
    toast({ title: 'Parties Updated', description: 'Shipper set to Actual Exporter, Consignee set to Actual Buyer.' });
  };

  const set = (field, value) => setFormData(p => ({ ...p, [field]: value }));

  const handleSave = async () => {
    try {
      setSaving(true);
      const url = isSaved
        ? `${backendUrl}/api/dockets/${docketId}/documents/${DOC_TYPE}`
        : `${backendUrl}/api/dockets/${docketId}/documents`;
      const res = await fetch(url, {
        method: isSaved ? 'PUT' : 'POST',
        headers: getAuth(),
        body: JSON.stringify(isSaved ? { document_data: formData } : { document_type: DOC_TYPE, document_data: formData })
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Save failed'); }
      const saved = await res.json();
      setDocumentId(saved.id);
      setIsSaved(true);
      toast({ title: 'Success', description: `${formTitle} saved successfully. You can now generate the PDF.` });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleGeneratePDF = async (copyType = 'ALL', saveToDrive = false) => {
    if (!isSaved) {
      toast({ title: 'Save Required', description: 'Please save the AWB form before generating PDF', variant: 'destructive' });
      return;
    }

    try {
      setGenerating(true);
      
      // Step 1: Render the SVG to a PDF buffer in frontend
      setShowHiddenPrint(true);
      await new Promise(resolve => {
        hiddenPrintPromiseResolve.current = resolve;
      });

      if (!printContainerRef.current) {
        throw new Error("Print container not found");
      }
      const el = printContainerRef.current;
      el.style.left = '0';
      // Give it a moment to ensure fonts and layout are applied
      await new Promise(r => setTimeout(r, 1500));
      let pdfBuffer;
      try {
        pdfBuffer = await generateDocPdf(el);
      } finally {
        el.style.left = '-9999px';
        setShowHiddenPrint(false);
      }
      
      const filename = `AWB_${docketInfo?.docket?.system_docket_no || docketId}_${copyType}.pdf`;

      if (saveToDrive) {
        // Upload to backend
        const fd = new FormData();
        const blob = new Blob([pdfBuffer], { type: 'application/pdf' });
        fd.append('file', blob, filename);
        fd.append('document_type', DOC_TYPE);
        fd.append('copy_type', copyType);
        
        const response = await fetch(
          `${backendUrl}/api/dockets/${docketId}/awb/upload-generated-pdf`,
          {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` },
            body: fd
          }
        );
        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.detail || "Failed to save to Drive");
        }
        
        const result = await response.json();
        
        toast({ 
          title: 'Success', 
          description: `AWB PDF (${copyType}) generated and saved to Google Drive`
        });
      } else {
        // Just download
        const url = window.URL.createObjectURL(new Blob([pdfBuffer]));
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => window.URL.revokeObjectURL(url), 5000);
        
        toast({ title: 'Success', description: `AWB PDF (${copyType}) generated successfully` });
      }

    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setGenerating(false);
    }
  };

  const handlePreviewPDF = () => {
    if (!isSaved) {
      toast({ title: 'Save Required', description: 'Please save the AWB form before previewing PDF', variant: 'destructive' });
      return;
    }
    // Open dedicated preview page synchronously (popup blockers allow direct user-click navigation)
    window.open(`/dockets/${docketId}/awb-preview?document_type=${DOC_TYPE}`, '_blank');
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;

  return (
    <>
    <div className="min-h-screen bg-surface-light p-6">
      <div className="max-w-6xl mx-auto space-y-5">

        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-text-dark font-manrope flex items-center gap-2">
                <FileOutput className="w-5 h-5 text-cyan-600" />
                {formTitle}
              </h1>
              <p className="text-sm text-text-secondary font-ibm">
                Docket: {docketInfo?.docket?.system_docket_no || docketId}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700" data-testid="awb-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {saving ? 'Saving...' : 'Save'}
            </Button>
            {isSaved && (
              <>

                <Button 
                  variant="outline" 
                  onClick={() => window.open(`/dockets/${docketId}/awb-svg-print?document_type=${DOC_TYPE}&draft=true`, '_blank')}
                  disabled={!isSaved}
                  data-testid="awb-draft-print-btn"
                  className="border-blue-600 text-blue-600 hover:bg-blue-50"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Draft Copy
                </Button>

                <Button 
                  variant="outline" 
                  onClick={() => window.open(`/dockets/${docketId}/awb-svg-print?document_type=${DOC_TYPE}`, '_blank')}
                  disabled={!isSaved}
                  data-testid="awb-svg-print-btn"
                  className="border-violet-600 text-violet-600 hover:bg-violet-50"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print Airway Bill
                </Button>

                <Button 
                  onClick={() => handleGeneratePDF('ALL', true)} 
                  disabled={generating}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />}
                  Generate & Save to Drive
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowEmailModal(true)}
                  className="border-orange-500 text-orange-600 hover:bg-orange-50"
                  data-testid="awb-send-email-btn"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Send Mail
                </Button>
              </>
            )}
          </div>
        </div>

        {/* ── Header Section ── */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-cyan-600 to-cyan-500 px-5 py-3">
            <h2 className="text-base font-semibold text-white font-ibm">AIR WAYBILL</h2>
          </div>
          <div className="p-5 grid grid-cols-2 gap-4">
            <Field label="Job No.">
              <Input value={formData.job_no} readOnly className="bg-gray-50 cursor-not-allowed" data-testid="awb-job-no" />
            </Field>
            <Field label="Master Air Waybill No.">
              <Input value={formData.master_awb_no} onChange={e => set('master_awb_no', e.target.value)} data-testid="awb-mawb-no" />
            </Field>
          </div>
        </Card>

        {/* ── Delivery Info ── */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gray-50 border-b border-gray-200 px-5 py-3 flex items-center gap-2">
            <FileOutput className="w-4 h-4 text-gray-500" />
            <h2 className="text-sm font-semibold text-text-dark font-ibm">Delivery Info</h2>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-2 gap-6">

              {/* Left Column */}
              <div className="space-y-4">
                {!isHouse && (
                  <div className="flex flex-wrap items-center gap-2 p-2.5 bg-blue-50/80 border border-blue-200 rounded-lg text-xs">
                    <span className="font-semibold text-blue-900 flex items-center gap-1">
                      Fill MAWB Parties:
                    </span>
                    <button
                      type="button"
                      onClick={applyForwarderParties}
                      className="px-2.5 py-1 bg-white hover:bg-blue-100 text-blue-700 font-semibold rounded border border-blue-300 shadow-sm transition-all text-xs"
                      title="Set Stavan as Shipper, Destination Agent as Consignee"
                    >
                      🏢 Stavan ➔ Destination Agent (Forwarder Standard)
                    </button>
                    <button
                      type="button"
                      onClick={applyClientParties}
                      className="px-2.5 py-1 bg-white hover:bg-gray-100 text-gray-700 font-semibold rounded border border-gray-300 shadow-sm transition-all text-xs"
                      title="Set Actual Exporter as Shipper, Actual Buyer as Consignee"
                    >
                      👤 Actual Exporter ➔ Actual Buyer (Direct)
                    </button>
                  </div>
                )}
                <Field label="Shipper's Name & Address">
                  <Textarea value={formData.shipper_name_address} onChange={e => set('shipper_name_address', e.target.value)} className="h-24" data-testid="awb-shipper-address" />
                </Field>
                <Field label="Shipper's Account No.">
                  <Input value={formData.shipper_account_no} onChange={e => set('shipper_account_no', e.target.value)} placeholder="Shipper's Account No." data-testid="awb-shipper-account" />
                </Field>
                <Field label="Consignee's Name & Address">
                  <Textarea value={formData.consignee_name_address} onChange={e => set('consignee_name_address', e.target.value)} className="h-24" data-testid="awb-consignee-address" />
                </Field>
                <Field label="Consignee's Account No.">
                  <Input value={formData.consignee_account_no} onChange={e => set('consignee_account_no', e.target.value)} placeholder="Consignee's Account No." data-testid="awb-consignee-account" />
                </Field>
                <Field label="Issuing Carrier's Agent Name & City">
                  <Textarea value={formData.issuing_agent_name_city} onChange={e => set('issuing_agent_name_city', e.target.value)} className="h-16" data-testid="awb-agent-city" />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Agent's IATA Code">
                    <Input value={formData.agent_iata_code} onChange={e => set('agent_iata_code', e.target.value)} data-testid="awb-iata-code" />
                  </Field>
                  <Field label="Account No.">
                    <Input value={formData.account_no} onChange={e => set('account_no', e.target.value)} placeholder="Account No." data-testid="awb-account-no" />
                  </Field>
                </div>
                <Field label="Airport of Departure & Requested Routing">
                  <Input value={formData.airport_of_departure} onChange={e => set('airport_of_departure', e.target.value)} data-testid="awb-airport-departure" />
                </Field>

                {/* Routing Table */}
                <div>
                  <Label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide font-ibm">Routing</Label>
                  <div className="mt-1 border border-gray-200 rounded overflow-hidden">
                    <div className="grid grid-cols-6 bg-gray-50 text-[10px] font-semibold text-gray-500 uppercase">
                      <div className="px-2 py-1.5 border-r border-gray-200">To</div>
                      <div className="px-2 py-1.5 border-r border-gray-200 col-span-1">By First Carrier</div>
                      <div className="px-2 py-1.5 border-r border-gray-200">To</div>
                      <div className="px-2 py-1.5 border-r border-gray-200">By</div>
                      <div className="px-2 py-1.5 border-r border-gray-200">To</div>
                      <div className="px-2 py-1.5">By</div>
                    </div>
                    <div className="grid grid-cols-6">
                      {[
                        ['routing_to_1','awb-rt1'], ['routing_by_1','awb-rb1'],
                        ['routing_to_2','awb-rt2'], ['routing_by_2','awb-rb2'],
                        ['routing_to_3','awb-rt3'], ['routing_by_3','awb-rb3'],
                      ].map(([field, tid], i) => (
                        <input key={field} value={formData[field]} onChange={e => set(field, e.target.value)}
                          className={`px-2 py-1.5 text-xs border-t border-gray-200 outline-none focus:bg-blue-50 ${i < 5 ? 'border-r border-gray-200' : ''}`}
                          data-testid={tid} />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Airport of Destination row */}
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Airport of Destination" className="col-span-1">
                    <Input value={formData.airport_of_destination} onChange={e => set('airport_of_destination', e.target.value)} data-testid="awb-airport-dest" />
                  </Field>
                  <Field label="Requested Flight">
                    <Input value={formData.requested_flight} onChange={e => set('requested_flight', e.target.value)} data-testid="awb-req-flight" />
                  </Field>
                  <Field label="Requested Date">
                    <Input type="date" value={formData.requested_date} onChange={e => set('requested_date', e.target.value)} data-testid="awb-req-date" />
                  </Field>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Not Negotiable">
                    <Input value={formData.not_negotiable} onChange={e => set('not_negotiable', e.target.value)} data-testid="awb-not-neg" />
                  </Field>
                  <Field label="Air Waybill Issued By">
                    <Input value={formData.air_waybill_issued_by} onChange={e => set('air_waybill_issued_by', e.target.value)} data-testid="awb-issued-by" />
                  </Field>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded p-2 text-[10px] text-gray-600 leading-relaxed">
                  Copies 1, 2 and 3 of this Air Waybill are originals and have the same validity.<br /><br />
                  It is agreed that the goods described herein are accepted in apparent good order and condition (except as noted) for carriage SUBJECT TO THE CONDITIONS OF CONTRACT ON THE REVERSE HEREOF. ALL GOODS MAY BE CARRIED BY ANY OTHER MEANS INCLUDING ROAD OR ANY OTHER CARRIER UNLESS SPECIFIC CONTRARY INSTRUCTIONS ARE GIVEN HEREIN BY THE SHIPPER, AND SHIPPER AGREES THAT THE SHIPMENT MAY BE CARRIED VIA INTERMEDIATE STOPPING PLACES WHICH THE CARRIER DEEMS APPROPRIATE.
                </div>
                <Field label="Accounting Information">
                  <Textarea value={formData.accounting_information} onChange={e => set('accounting_information', e.target.value)} className="h-24" data-testid="awb-accounting-info" />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Reference Number">
                    <Input value={formData.reference_number} onChange={e => set('reference_number', e.target.value)} placeholder="Reference Number" data-testid="awb-ref-no" />
                  </Field>
                  <Field label="Optional Shipping Information">
                    <Textarea value={formData.optional_shipping_info} onChange={e => set('optional_shipping_info', e.target.value)} className="h-16" data-testid="awb-opt-ship" />
                  </Field>
                </div>

                {/* Currency & Charges */}
                <div className="grid grid-cols-6 gap-2">
                  <Field label="Currency"><Input value={formData.currency} onChange={e => set('currency', e.target.value)} data-testid="awb-currency" /></Field>
                  <Field label="CHGS Code"><Input value={formData.chgs_code} onChange={e => set('chgs_code', e.target.value)} data-testid="awb-chgs-code" /></Field>
                  <Field label="PPD (WT)"><Input value={formData.ppd_wt} onChange={e => set('ppd_wt', e.target.value)} data-testid="awb-ppd-wt" /></Field>
                  <Field label="COLL (WT)"><Input value={formData.coll_wt} onChange={e => set('coll_wt', e.target.value)} data-testid="awb-coll-wt" /></Field>
                  <Field label="PPD (OTHER)"><Input value={formData.ppd_other} onChange={e => set('ppd_other', e.target.value)} data-testid="awb-ppd-other" /></Field>
                  <Field label="COLL (OTHER)"><Input value={formData.coll_other} onChange={e => set('coll_other', e.target.value)} data-testid="awb-coll-other" /></Field>
                </div>

                {/* Declared Values */}
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Declared Value for Carriage">
                    <Input value={formData.declared_value_carriage} onChange={e => set('declared_value_carriage', e.target.value)} data-testid="awb-decl-carriage" />
                  </Field>
                  <Field label="Declared Value for Customs">
                    <Input value={formData.declared_value_customs} onChange={e => set('declared_value_customs', e.target.value)} placeholder="Declared value for customs" data-testid="awb-decl-customs" />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Amount of Insurance">
                    <Input value={formData.amount_of_insurance} onChange={e => set('amount_of_insurance', e.target.value)} placeholder="Amount of Insurance" data-testid="awb-insurance" />
                  </Field>
                  <Field label="SCI">
                    <Input value={formData.sci} onChange={e => set('sci', e.target.value)} placeholder="SCI" data-testid="awb-sci" />
                  </Field>
                </div>
                <div className="text-[10px] text-gray-500 italic bg-gray-50 rounded p-2 border border-gray-200">
                  INSURANCE: If Carrier offers insurance, and such insurance is requested in accordance with the conditions thereof, indicate amount to be insured in figures in box marked 'Amount of Insurance'.
                </div>

                {/* Handling Information */}
                <Field label="Handling Information">
                  <Textarea value={formData.handling_information} onChange={e => set('handling_information', e.target.value)} className="h-20" data-testid="awb-handling" />
                </Field>
              </div>
            </div>
          </div>
        </Card>

        {/* ── Package Info ── */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gray-50 border-b border-gray-200 px-5 py-3">
            <h2 className="text-sm font-semibold text-text-dark font-ibm">Package Info</h2>
          </div>
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-9 gap-2">
              <Field label="No. of Pieces RCP"><Input value={formData.no_of_pieces_rcp} onChange={e => set('no_of_pieces_rcp', e.target.value)} data-testid="awb-pieces" /></Field>
              <Field label="Gross Weight"><Input value={formData.gross_weight} onChange={e => set('gross_weight', e.target.value)} data-testid="awb-gross-wt" /></Field>
              <Field label="Kg / Lb"><Input value={formData.kg_lb} onChange={e => set('kg_lb', e.target.value)} data-testid="awb-kg-lb" /></Field>
              <Field label="Rate Class"><Input value={formData.rate_class} onChange={e => set('rate_class', e.target.value)} placeholder="Rate Class" data-testid="awb-rate-class" /></Field>
              <Field label="Commodity Item No."><Input value={formData.commodity_item_no} onChange={e => set('commodity_item_no', e.target.value)} placeholder="Commodity Item no." data-testid="awb-commodity" /></Field>
              <Field label="Chargeable Weight"><Input value={formData.chargeable_weight} onChange={e => set('chargeable_weight', e.target.value)} data-testid="awb-chargeable-wt" /></Field>
              <Field label="Rate / Charge"><Input value={formData.rate_charge} onChange={e => set('rate_charge', e.target.value)} data-testid="awb-rate-charge" /></Field>
              <Field label="Total"><Input value={formData.total} onChange={e => set('total', e.target.value)} placeholder="NaN" data-testid="awb-total" /></Field>
              <Field label="Nature & Qty (Short)">
                <Textarea value={formData.nature_quantity_short} onChange={e => set('nature_quantity_short', e.target.value)} className="h-16 text-xs" data-testid="awb-nature-short" />
              </Field>
            </div>
            <Field label="Nature & Quantity of Goods">
              <Textarea value={formData.nature_quantity_goods} onChange={e => set('nature_quantity_goods', e.target.value)} className="h-24" data-testid="awb-nature-goods" />
            </Field>
          </div>
        </Card>

        {/* ── Other Info ── */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gray-50 border-b border-gray-200 px-5 py-3">
            <h2 className="text-sm font-semibold text-text-dark font-ibm">Other Info</h2>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-2 gap-6">

              {/* Left: Charges */}
              <div className="space-y-3">
                {/* Weight Charge */}
                <div className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wide font-ibm">Weight Charge</div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Prepaid"><Input value={formData.weight_charge_prepaid} onChange={e => set('weight_charge_prepaid', e.target.value)} data-testid="awb-wc-prepaid" /></Field>
                  <Field label="Collect"><Input value={formData.weight_charge_collect} onChange={e => set('weight_charge_collect', e.target.value)} placeholder="Collect Weight Charge" data-testid="awb-wc-collect" /></Field>
                </div>
                <div className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wide font-ibm">Valuation Charge</div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label=""><Input value={formData.valuation_charge_prepaid} onChange={e => set('valuation_charge_prepaid', e.target.value)} placeholder="Prepaid Valuation Charge" data-testid="awb-vc-prepaid" /></Field>
                  <Field label=""><Input value={formData.valuation_charge_collect} onChange={e => set('valuation_charge_collect', e.target.value)} placeholder="Collect Valuation Charge" data-testid="awb-vc-collect" /></Field>
                </div>
                <div className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wide font-ibm">Tax</div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label=""><Input value={formData.tax_prepaid} onChange={e => set('tax_prepaid', e.target.value)} placeholder="Prepaid Tax" data-testid="awb-tax-prepaid" /></Field>
                  <Field label=""><Input value={formData.tax_collect} onChange={e => set('tax_collect', e.target.value)} placeholder="Collect Tax" data-testid="awb-tax-collect" /></Field>
                </div>
                <div className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wide font-ibm">Total Other Charges Due Agent</div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label=""><Input value={formData.total_other_charges_agent_prepaid} onChange={e => set('total_other_charges_agent_prepaid', e.target.value)} placeholder="AS AGREED" data-testid="awb-agent-prepaid" /></Field>
                  <Field label=""><Input value={formData.total_other_charges_agent_collect} onChange={e => set('total_other_charges_agent_collect', e.target.value)} placeholder="Total Other Charges Due Agent(Collect)" data-testid="awb-agent-collect" /></Field>
                </div>
                <div className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wide font-ibm">Total Other Charges Due Carrier</div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label=""><Input value={formData.total_other_charges_carrier_prepaid} onChange={e => set('total_other_charges_carrier_prepaid', e.target.value)} placeholder="Total Other Charges Due Carrier(Prepaid)" data-testid="awb-carrier-prepaid" /></Field>
                  <Field label=""><Input value={formData.total_other_charges_carrier_collect} onChange={e => set('total_other_charges_carrier_collect', e.target.value)} placeholder="Total Other Charges Due Carrier(Collect)" data-testid="awb-carrier-collect" /></Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Total Prepaid"><Input value={formData.total_prepaid} onChange={e => set('total_prepaid', e.target.value)} data-testid="awb-total-prepaid" /></Field>
                  <Field label="Total Collect"><Input value={formData.total_collect} onChange={e => set('total_collect', e.target.value)} placeholder="Total Collect" data-testid="awb-total-collect" /></Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Currency Conversion Rates"><Input value={formData.currency_conversion_rates} onChange={e => set('currency_conversion_rates', e.target.value)} placeholder="Currency Conversion Rates" data-testid="awb-conv-rates" /></Field>
                  <Field label="CC Charges in Dest Currency"><Input value={formData.cc_charges_dest_currency} onChange={e => set('cc_charges_dest_currency', e.target.value)} placeholder="CC Charges in Dest Currency" data-testid="awb-cc-charges" /></Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="For Carrier's Use only at Destination"><Input value={formData.for_carrier_use_destination} onChange={e => set('for_carrier_use_destination', e.target.value)} data-testid="awb-carrier-dest" /></Field>
                  <Field label="Charges at Destination"><Input value={formData.charges_at_destination} onChange={e => set('charges_at_destination', e.target.value)} placeholder="Charges at Destination" data-testid="awb-charges-dest" /></Field>
                </div>
              </div>

              {/* Right: Other Charges + Certification + Signature */}
              <div className="space-y-3">
                <Field label="Other Charges">
                  <Textarea value={formData.other_charges} onChange={e => set('other_charges', e.target.value)} className="h-28" placeholder="Other Charges" data-testid="awb-other-charges" />
                </Field>
                <div className="bg-gray-50 border border-gray-200 rounded p-3 text-[10px] text-gray-700 leading-relaxed h-28 overflow-y-auto">
                  Shipper certifies that the particulars on the face hereof are correct and that insofar as any part of the consignment contains dangerous goods, such part is properly described and is in proper condition for carriage by air according to the applicable Dangerous Goods Regulations.
                </div>
                <Field label="Signature of Shipper or his Agent">
                  <Input value={formData.signature_shipper} onChange={e => set('signature_shipper', e.target.value)} data-testid="awb-sig-shipper" />
                </Field>
                <div className="grid grid-cols-3 gap-2">
                  <Field label="Executed On">
                    <Input type="date" value={formData.executed_on} onChange={e => set('executed_on', e.target.value)} data-testid="awb-executed-on" />
                  </Field>
                  <Field label="At (Place)">
                    <Input value={formData.at_place} onChange={e => set('at_place', e.target.value)} placeholder="At(Place)" data-testid="awb-at-place" />
                  </Field>
                  <Field label="Signature of Issuing Carrier or its Agent">
                    <Input value={formData.signature_issuing_carrier} onChange={e => set('signature_issuing_carrier', e.target.value)} data-testid="awb-sig-carrier" />
                  </Field>
                </div>
                <Field label="Total Collect Charges">
                  <Input value={formData.total_collect_charges} onChange={e => set('total_collect_charges', e.target.value)} placeholder="Total Collect Charges" data-testid="awb-total-collect-charges" />
                </Field>
              </div>
            </div>
          </div>
        </Card>

        {/* Saved status */}
        {isSaved && (
          <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-cyan-700 font-ibm">
                <span className="font-semibold">✓ Saved</span> — {formTitle} linked to docket {docketInfo?.docket?.system_docket_no}
              </p>
              <span className="text-xs text-cyan-600">ID: {documentId?.toString().slice(0, 8)}...</span>
            </div>
            <div className="mt-3 p-3 bg-white rounded border border-cyan-100">
              <p className="text-xs text-gray-600 font-semibold mb-2">📄 PDF Generation Options:</p>
              <ul className="text-xs text-gray-600 space-y-1 ml-4">

                <li>• <strong>Generate & Save to Drive:</strong> Create PDF and automatically save to Google Drive in docket folder</li>
              </ul>
            </div>
          </div>
        )}

      </div>
    </div>

    {/* Hidden Print Container */}
    <div 
      ref={printContainerRef}
      style={{
        position: 'absolute',
        top: 0,
        left: '-9999px',
        width: '210mm',
        backgroundColor: '#fff',
        zIndex: -1
      }}
    >
      {showHiddenPrint && (
        <AirWaybillSVGPrint 
          hiddenMode={true} 
          documentType={DOC_TYPE} 
          onReady={() => {
            if (hiddenPrintPromiseResolve.current) {
              hiddenPrintPromiseResolve.current();
            }
          }}
        />
      )}
    </div>

    {/* Send Email Modal */}
    <SendEmailModal
      isOpen={showEmailModal}
      onClose={() => setShowEmailModal(false)}
      templateKey="air_waybill"
      variables={{
        docket_no: formData.job_no || docketId,
        awb_no: formData.master_awb_no || '',
        shipper_name: (formData.shipper_name_address || '').split('\n')[0],
        consignee_name: (formData.consignee_name_address || '').split('\n')[0],
        airport_departure: formData.airport_of_departure || '',
        airport_destination: formData.airport_of_destination || '',
      }}
      defaultToEmail={docketInfo?.resolved_parties?.importer_receiver?.email || ''}
      defaultSubject={`${formTitle} — ${docketInfo?.docket?.system_docket_no || ''}`}
      pdfFetcher={async () => {
        const token = localStorage.getItem('auth_token');
        const res = await fetch(
          `${backendUrl}/api/dockets/${docketId}/awb/preview-pdf?token=${token}`,
          { cache: 'no-store' }
        );
        if (!res.ok) throw new Error('PDF unavailable');
        const buf = await res.arrayBuffer();
        return new Uint8Array(buf);
      }}
      pdfFilename={`AIRWAY BILL - ${docketInfo?.docket?.system_docket_no || docketId}.pdf`}
      documentTitle={`AIRWAY BILL - ${docketInfo?.docket?.system_docket_no || docketId}`}
      docketId={docketId}
    />
    </>
  );
};

export default AirWaybillForm;
